<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  reactive,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import SpaceRow from './SpaceRow.vue';
import VenueRow from './VenueRow.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import SpaceDialog from './SpaceDialog.vue';
import VenueDialog from './VenueDialog.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { isCloudFeatureEnabled } = useAccount();
const isBuzzEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.BUZZ));

const TABS = ['spaces', 'venues'];
const activeTab = ref('spaces');

const loading = computed(() => store.state.spaces.uiFlags.isFetching);
const venuesLoading = computed(() => store.state.venues.uiFlags.isFetching);

const compareSpacesByName = (a, b) =>
  (a?.name || '').localeCompare(b?.name || '', undefined, {
    sensitivity: 'base',
    numeric: true,
  });

const spaces = computed(() => {
  const allSpaces = store.getters['spaces/getSpaces'] || [];
  const topLevel = allSpaces
    .filter(space => !space.parent_id)
    .sort(compareSpacesByName);

  const childrenByParentId = allSpaces.reduce((groups, space) => {
    if (!space.parent_id) return groups;
    if (!groups[space.parent_id]) groups[space.parent_id] = [];
    groups[space.parent_id].push(space);
    return groups;
  }, {});

  Object.values(childrenByParentId).forEach(children =>
    children.sort(compareSpacesByName)
  );

  const ordered = [];
  topLevel.forEach(parent => {
    ordered.push(parent);
    (childrenByParentId[parent.id] || []).forEach(child => ordered.push(child));
  });

  const orderedIds = new Set(ordered.map(space => space.id));
  allSpaces
    .filter(space => !orderedIds.has(space.id))
    .sort(compareSpacesByName)
    .forEach(space => ordered.push(space));

  return ordered;
});

const venues = computed(() =>
  [...(store.getters['venues/getVenues'] || [])].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  )
);

const catalogue = computed(() => store.getters['spaces/getCatalogue']);

const spaceCountByVenueId = computed(() =>
  (store.getters['spaces/getSpaces'] || []).reduce((acc, space) => {
    if (!space.venue_id) return acc;
    acc[space.venue_id] = (acc[space.venue_id] || 0) + 1;
    return acc;
  }, {})
);

const venueBuzzById = computed(() => {
  if (!isBuzzEnabled.value) return {};
  return venues.value.reduce((acc, venue) => {
    acc[venue.id] = venue.buzz_score;
    return acc;
  }, {});
});

const isSpacesTab = computed(() => activeTab.value === 'spaces');
const isVenuesTab = computed(() => activeTab.value === 'venues');

const tabsForTabBar = computed(() => [
  {
    key: 'spaces',
    label: t('SETTINGS.SPACES.TABS.SPACES'),
    count: spaces.value.length,
  },
  {
    key: 'venues',
    label: t('SETTINGS.SPACES.TABS.VENUES'),
    count: venues.value.length,
  },
]);

const activeTabIndex = computed(() =>
  Math.max(TABS.indexOf(activeTab.value), 0)
);

const onTabChange = tab => {
  activeTab.value = tab.key;
  router.replace({ query: { ...route.query, tab: tab.key } });
};

watch(
  () => route.query.tab,
  tab => {
    if (tab === 'venues') activeTab.value = 'venues';
    else if (tab === 'spaces') activeTab.value = 'spaces';
  },
  { immediate: true }
);

// ── Venue create flapout ────────────────────────────────────────────────────
const isPopoverOpen = ref(false);
const popoverRef = ref(null);
const popoverPosition = reactive({ top: 0, left: 0 });
const popoverTriggerRect = ref(null);

const PANEL_WIDTH = 512;
const VIEWPORT_SPACING = 8;

const setPopoverPositionFromTrigger = triggerEl => {
  if (!triggerEl) return;
  const rect = triggerEl.getBoundingClientRect();
  popoverTriggerRect.value = rect;
  popoverPosition.top = rect.bottom + VIEWPORT_SPACING;
  popoverPosition.left = Math.min(
    Math.max(VIEWPORT_SPACING, rect.right - PANEL_WIDTH),
    window.innerWidth - PANEL_WIDTH - VIEWPORT_SPACING
  );
};

const updatePopoverPosition = () => {
  if (!popoverTriggerRect.value || !popoverRef.value) return;
  const triggerRect = popoverTriggerRect.value;
  const panelRect = popoverRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_SPACING;
  const spaceAbove = triggerRect.top - VIEWPORT_SPACING;
  if (panelRect.height > spaceBelow && spaceAbove > spaceBelow) {
    popoverPosition.top = Math.max(
      VIEWPORT_SPACING,
      triggerRect.top - panelRect.height - VIEWPORT_SPACING
    );
    return;
  }
  popoverPosition.top = Math.min(
    triggerRect.bottom + VIEWPORT_SPACING,
    window.innerHeight - panelRect.height - VIEWPORT_SPACING
  );
};

const openCreateVenueFlapout = async event => {
  setPopoverPositionFromTrigger(event?.currentTarget);
  isPopoverOpen.value = true;
  await nextTick();
  updatePopoverPosition();
};

const dismissFlapout = () => {
  isPopoverOpen.value = false;
};

const handleVenueCreated = venue => {
  dismissFlapout();
  store.dispatch('venues/get');
  if (venue?.id) {
    router.push({
      name: 'settings_venues_show',
      params: { venueId: venue.id },
    });
  }
};

const handleVenueEscape = e => {
  if (isPopoverOpen.value && e.key === 'Escape') dismissFlapout();
};

// ── Venue list actions ──────────────────────────────────────────────────────
const showDeleteVenueModal = ref(false);
const selectedVenue = ref(null);
const deletingVenueLoading = ref({});

const handleEditVenue = venue => {
  router.push({
    name: 'settings_venues_show',
    params: { venueId: venue.id },
  });
};

const openDeleteVenueModal = venue => {
  selectedVenue.value = venue;
  showDeleteVenueModal.value = true;
};

const closeDeleteVenueModal = () => {
  showDeleteVenueModal.value = false;
  selectedVenue.value = null;
};

const confirmDeleteVenue = async () => {
  if (!selectedVenue.value) return;
  deletingVenueLoading.value[selectedVenue.value.id] = true;
  try {
    await store.dispatch('venues/delete', selectedVenue.value.id);
    closeDeleteVenueModal();
    await store.dispatch('spaces/get');
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('SETTINGS.SPACES.VENUES.DELETE_ERROR')
    );
  } finally {
    deletingVenueLoading.value[selectedVenue.value?.id] = false;
  }
};

// ── Venue assignment from space row ─────────────────────────────────────────
const updateSpaceVenue = async (space, venueId) => {
  try {
    await store.dispatch('spaces/update', {
      id: space.id,
      venue_id: venueId || null,
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('SETTINGS.SPACES.VENUE_ASSIGN_ERROR')
    );
  }
};

// ── Space delete / create ─────────────────────────────────────────────────
const selectedSpace = ref(null);
const showDeleteModal = ref(false);
const deletingLoading = ref({});
const showCreateDialog = ref(false);
const createDialogRef = ref(null);
const createDialogPosition = reactive({ top: 0, left: 0 });
const createDialogTriggerRect = ref(null);

const CREATE_PANEL_WIDTH = 400;

const setCreateDialogPositionFromTrigger = triggerEl => {
  if (!triggerEl) return;
  const rect = triggerEl.getBoundingClientRect();
  createDialogTriggerRect.value = rect;
  createDialogPosition.top = rect.bottom + VIEWPORT_SPACING;
  createDialogPosition.left = Math.min(
    Math.max(VIEWPORT_SPACING, rect.right - CREATE_PANEL_WIDTH),
    window.innerWidth - CREATE_PANEL_WIDTH - VIEWPORT_SPACING
  );
};

const updateCreateDialogPosition = () => {
  if (!createDialogTriggerRect.value || !createDialogRef.value) return;
  const panelEl = createDialogRef.value.$el;
  if (!panelEl) return;
  const triggerRect = createDialogTriggerRect.value;
  const panelRect = panelEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_SPACING;
  const spaceAbove = triggerRect.top - VIEWPORT_SPACING;
  if (panelRect.height > spaceBelow && spaceAbove > spaceBelow) {
    createDialogPosition.top = Math.max(
      VIEWPORT_SPACING,
      triggerRect.top - panelRect.height - VIEWPORT_SPACING
    );
    return;
  }
  createDialogPosition.top = Math.min(
    triggerRect.bottom + VIEWPORT_SPACING,
    window.innerHeight - panelRect.height - VIEWPORT_SPACING
  );
};

const openCreateSpaceDialog = async event => {
  setCreateDialogPositionFromTrigger(event?.currentTarget);
  showCreateDialog.value = true;
  await nextTick();
  updateCreateDialogPosition();
};

const closeCreateDialog = () => {
  showCreateDialog.value = false;
};

const cancelCreateDialog = () => {
  createDialogRef.value?.resetForm();
  showCreateDialog.value = false;
};

const handleSpaceCreated = space => {
  closeCreateDialog();
  store.dispatch('spaces/get');
  if (space?.id) {
    router.push({
      name: 'settings_spaces_show',
      params: { spaceId: space.id },
    });
  }
};

const handleEditSpace = space => {
  router.push({ name: 'settings_spaces_show', params: { spaceId: space.id } });
};

const handleDeleteSpace = space => {
  selectedSpace.value = space;
  showDeleteModal.value = true;
};

const confirmDeleteSpace = async () => {
  if (!selectedSpace.value) return;
  deletingLoading.value[selectedSpace.value.id] = true;
  try {
    await store.dispatch('spaces/delete', selectedSpace.value.id);
    showDeleteModal.value = false;
    selectedSpace.value = null;
  } catch (error) {
    const errorCode = error?.response?.data?.error;
    const experiences = error?.response?.data?.experiences;
    if (errorCode === 'SPACE_IN_USE_BY_EXPERIENCE') {
      useAlert(
        t('SETTINGS.SPACES.DELETE_ERROR_IN_USE', {
          experiences: experiences || '',
        })
      );
    } else {
      useAlert(
        error?.response?.data?.errors?.base ||
          error?.message ||
          t('SETTINGS.SPACES.DELETE_ERROR')
      );
    }
  } finally {
    deletingLoading.value[selectedSpace.value?.id] = false;
  }
};

const cancelDeleteSpace = () => {
  showDeleteModal.value = false;
  selectedSpace.value = null;
};

const navigateToCatalogue = () => {
  if (catalogue.value?.id) {
    router.push({
      name: 'settings_knowledgebases_content',
      params: { knowledgebaseId: catalogue.value.id },
    });
  }
};

onMounted(async () => {
  document.addEventListener('keydown', handleVenueEscape);
  await Promise.all([
    store.dispatch('spaces/get'),
    store.dispatch('venues/get'),
  ]);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleVenueEscape);
});
</script>

<template>
  <SettingsLayout
    :is-loading="isSpacesTab ? loading : venuesLoading"
    :loading-message="$t('PLANNING.CALENDAR.LOADING')"
    :no-records-found="isSpacesTab ? !spaces.length : !venues.length"
    :no-records-message="
      isSpacesTab
        ? $t('SETTINGS.SPACES.NO_SPACES_MESSAGE')
        : $t('SETTINGS.SPACES.NO_VENUES_MESSAGE')
    "
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SETTINGS.SPACES.TITLE')"
        :description="$t('SETTINGS.SPACES.PAGE_DESCRIPTION')"
      >
        <template #actions>
          <Button
            v-if="catalogue && isSpacesTab"
            icon="i-lucide-book-text"
            md
            slate
            outlined
            label="Catalogue"
            @click="navigateToCatalogue"
          />
          <Button
            v-if="isVenuesTab"
            icon="i-lucide-building-2"
            md
            :label="$t('SETTINGS.SPACES.VENUES.ADD')"
            @click="openCreateVenueFlapout"
          />
          <Button
            v-if="isSpacesTab"
            icon="i-lucide-plus"
            md
            :label="$t('SETTINGS.SPACES.CREATE')"
            @click="openCreateSpaceDialog"
          />
        </template>
      </BaseSettingsHeader>

      <div class="mt-4">
        <TabBar
          :tabs="tabsForTabBar"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChange"
        />
      </div>
    </template>

    <template #body>
      <!-- Spaces table -->
      <table v-if="isSpacesTab" class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak text-sm">
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.TABLE_HEADER.NAME') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.TABLE_HEADER.PARENT') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.TABLE_HEADER.VENUE') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.TABLE_HEADER.CAPACITY') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.TABLE_HEADER.CREATED_ON') }}
            </th>
            <th class="py-4 w-32" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <SpaceRow
            v-for="space in spaces"
            :key="space.id"
            :space="space"
            :venues="venues"
            :venue-buzz-by-id="venueBuzzById"
            :loading="deletingLoading[space.id]"
            @edit="handleEditSpace"
            @delete="handleDeleteSpace"
            @update-venue="updateSpaceVenue"
            @edit-venue="handleEditVenue"
          />
        </tbody>
      </table>

      <!-- Venues table -->
      <table v-else class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak text-sm">
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.VENUE_TABLE.NAME') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.VENUE_TABLE.LOCATION') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.VENUE_TABLE.SPACES') }}
            </th>
            <th
              v-if="isBuzzEnabled"
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.VENUE_TABLE.BUZZ') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 ltr:text-left rtl:text-right font-semibold text-n-slate-11"
            >
              {{ $t('SETTINGS.SPACES.VENUE_TABLE.CREATED_ON') }}
            </th>
            <th class="py-4 w-32" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <VenueRow
            v-for="venue in venues"
            :key="venue.id"
            :venue="venue"
            :space-count="spaceCountByVenueId[venue.id] || 0"
            :show-buzz="isBuzzEnabled"
            :loading="deletingVenueLoading[venue.id]"
            @edit="handleEditVenue"
            @delete="openDeleteVenueModal"
          />
        </tbody>
      </table>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-confirm="confirmDeleteSpace"
      :title="$t('SETTINGS.SPACES.DELETE_CONFIRM_TITLE')"
      :message="$t('SETTINGS.SPACES.DELETE_CONFIRM_MESSAGE')"
      :message-value="selectedSpace?.name"
      :confirm-text="$t('SETTINGS.SPACES.DELETE_CONFIRM_YES')"
      :reject-text="$t('SETTINGS.SPACES.DELETE_CONFIRM_NO')"
      @close="cancelDeleteSpace"
    />
  </SettingsLayout>

  <SpaceDialog
    v-show="showCreateDialog"
    ref="createDialogRef"
    v-on-click-outside="() => showCreateDialog && closeCreateDialog()"
    :active="showCreateDialog"
    :panel-style="{
      top: `${createDialogPosition.top}px`,
      left: `${createDialogPosition.left}px`,
    }"
    @close="closeCreateDialog"
    @cancel="cancelCreateDialog"
    @saved="handleSpaceCreated"
  />

  <!-- Venue create flapout -->
  <div
    v-show="isPopoverOpen"
    ref="popoverRef"
    v-on-click-outside="() => isPopoverOpen && dismissFlapout()"
    class="fixed z-50 w-[32rem] max-h-[90vh] overflow-y-auto rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg p-4"
    :style="{
      top: `${popoverPosition.top}px`,
      left: `${popoverPosition.left}px`,
    }"
  >
    <VenueDialog embedded @close="dismissFlapout" @saved="handleVenueCreated" />
  </div>

  <!-- Venue delete confirmation modal -->
  <div
    v-if="showDeleteVenueModal"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
    @click.self="closeDeleteVenueModal"
  >
    <div
      class="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-2xl p-6 mx-4"
    >
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ $t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_TITLE') }}
      </h3>
      <p class="mt-2 text-sm text-slate-600 dark:text-slate-300">
        {{ $t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_MESSAGE') }}
        <span class="font-semibold">{{ selectedVenue?.name }}</span>
      </p>
      <div class="mt-5 flex items-center gap-2">
        <Button
          :label="$t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_NO')"
          slate
          outlined
          class="w-full"
          @click="closeDeleteVenueModal"
        />
        <Button
          :label="$t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_YES')"
          ruby
          class="w-full"
          @click="confirmDeleteVenue"
        />
      </div>
    </div>
  </div>
</template>
